import { FC, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import { FormatoReuniao, Reuniao, ReuniaoRequest, StatusReuniao, transicoesValidas } from '@/types/reuniao'
import { ReuniaoFormFields, localPendente } from '@/components/agenda/ReuniaoFormFields'
import { reuniaoService } from '@/services/reuniaoService'
import { clienteService } from '@/services/clienteService'
import { oportunidadeService } from '@/services/oportunidadeService'
import { Cliente } from '@/types/cliente'
import { Oportunidade } from '@/types/oportunidade'
import { formatCNPJ } from '@/utils/formatters'
import { SeletorReservaReuniao, nomeDaReserva } from '@/components/agenda/SeletorReservaReuniao'

interface Prefill {
  oportunidadeId?: string
  clienteId?: string
  clienteNome?: string
  titulo?: string
  /** Pré-marca "Reunião de primeiro contato" (Pipeline: Reserva → Reunião só avança com ela). */
  primeiroContato?: boolean
}

interface ReuniaoDialogProps {
  open: boolean
  onClose: () => void
  reuniao?: Reuniao | null
  prefill?: Prefill
  onSaved?: (reuniao: Reuniao) => void
}

const STATUS_LABEL: Record<StatusReuniao, string> = {
  AGENDADA: 'Agendada',
  CONFIRMADA: 'Confirmada',
  REALIZADA: 'Realizada',
  CANCELADA: 'Cancelada',
  REAGENDADA: 'Reagendada',
}

// <input type="datetime-local"> exige formato YYYY-MM-DDTHH:mm
const toInputValue = (iso: string | undefined): string => {
  if (!iso) return ''
  return dayjs(iso).format('YYYY-MM-DDTHH:mm')
}

const defaultInicio = () => dayjs().add(1, 'hour').startOf('hour').format('YYYY-MM-DDTHH:mm')
const defaultFim = () => dayjs().add(2, 'hour').startOf('hour').format('YYYY-MM-DDTHH:mm')

export const ReuniaoDialog: FC<ReuniaoDialogProps> = ({
  open,
  onClose,
  reuniao,
  prefill,
  onSaved,
}) => {
  const { enqueueSnackbar } = useSnackbar()
  const editing = !!reuniao

  const [titulo, setTitulo] = useState('')
  const [descricao, setDescricao] = useState('')
  const [local, setLocal] = useState('')
  const [formato, setFormato] = useState<FormatoReuniao | ''>('')
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [observacoes, setObservacoes] = useState('')
  const [primeiroContato, setPrimeiroContato] = useState(false)
  const [participantes, setParticipantes] = useState('')
  const [status, setStatus] = useState<StatusReuniao>('AGENDADA')
  const [clienteSelecionado, setClienteSelecionado] = useState<Cliente | null>(null)
  const [clienteTextoLivre, setClienteTextoLivre] = useState('')
  const [oportunidadeId, setOportunidadeId] = useState<string | null>(null)
  // Reserva vinculada (seletor da Nova Reunião, 10/10/2026). `oportunidadeId` continua sendo a
  // fonte do payload; `reserva` é o que o seletor mostra (pode faltar se a consulta falhar).
  const [reserva, setReserva] = useState<Oportunidade | null>(null)
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [loadingClientes, setLoadingClientes] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  // Reseta/hidrata o form quando o diálogo abre
  useEffect(() => {
    if (!open) return
    setErro(null)
    if (reuniao) {
      setTitulo(reuniao.titulo)
      setDescricao(reuniao.descricao ?? '')
      setLocal(reuniao.local ?? '')
      setFormato(reuniao.formato ?? '')
      setDataInicio(toInputValue(reuniao.dataInicio))
      setDataFim(toInputValue(reuniao.dataFim))
      setObservacoes(reuniao.observacoes ?? '')
      setPrimeiroContato(reuniao.primeiroContato ?? false)
      setParticipantes(reuniao.participantes ?? '')
      setStatus(reuniao.status)
      setOportunidadeId(reuniao.oportunidadeId ?? null)
      setClienteTextoLivre(reuniao.clienteNome ?? '')
      setClienteSelecionado(null) // hidrata do texto; autocomplete popula ao carregar clientes
    } else {
      setTitulo(prefill?.titulo ?? '')
      setDescricao('')
      setPrimeiroContato(prefill?.primeiroContato ?? false)
      setParticipantes('')
      setLocal('')
      setFormato('')
      setDataInicio(defaultInicio())
      setDataFim(defaultFim())
      setObservacoes('')
      setStatus('AGENDADA')
      setOportunidadeId(prefill?.oportunidadeId ?? null)
      setClienteTextoLivre(prefill?.clienteNome ?? '')
      setClienteSelecionado(null)
    }
    setReserva(null)
  }, [open, reuniao, prefill])

  // Vínculo travado em dois casos: (1) o diálogo veio do contexto de uma reserva (Pipeline,
  // Estruturação); (2) edição de reunião já vinculada — o PUT ignora `oportunidadeId` nulo, então
  // "desvincular" na tela não desvincularia no servidor. Na Agenda, sem vínculo, o usuário escolhe.
  const vinculoFixo = editing ? !!reuniao?.oportunidadeId : !!prefill?.oportunidadeId

  // Hidrata a reserva já vinculada (edição ou prefill) para o seletor mostrar empresa e CNPJ.
  // Falha na consulta não bloqueia nada: o vínculo segue pelo `oportunidadeId`.
  useEffect(() => {
    if (!open) return
    const id = reuniao?.oportunidadeId ?? prefill?.oportunidadeId
    if (!id) return
    let ativo = true
    Promise.resolve()
      .then(() => oportunidadeService.buscar(id))
      .then((r) => { if (ativo && r) setReserva(r) })
      .catch(() => { /* mantém o vínculo sem os detalhes */ })
    return () => { ativo = false }
  }, [open, reuniao, prefill])

  const escolherReserva = (r: Oportunidade | null) => {
    setReserva(r)
    setOportunidadeId(r?.id ?? null)
    if (r) {
      const nome = nomeDaReserva(r)
      setClienteSelecionado(null)
      setClienteTextoLivre(nome)
      if (!titulo.trim()) setTitulo(`Reunião — ${nome}`)
    }
  }

  // Carrega lista de clientes para o autocomplete
  useEffect(() => {
    if (!open) return
    let ativo = true
    setLoadingClientes(true)
    clienteService
      .listar(0, 200)
      .then((page) => {
        if (!ativo) return
        const lista = page.content ?? []
        setClientes(lista)
        // Prefill: se já temos clienteId no prefill/reuniao, seleciona o item correspondente.
        const alvoId = reuniao?.clienteId ?? prefill?.clienteId
        if (alvoId) {
          const match = lista.find((c) => c.id === alvoId)
          if (match) setClienteSelecionado(match)
        }
      })
      .catch(() => {
        // autocomplete vira livre: usuário pode digitar o nome manualmente
      })
      .finally(() => {
        if (ativo) setLoadingClientes(false)
      })
    return () => {
      ativo = false
    }
  }, [open, reuniao, prefill])

  const canSubmit = useMemo(() => {
    if (!titulo.trim()) return false
    if (!dataInicio || !dataFim) return false
    // Presencial sem local: o backend recusaria (LOCAL_OBRIGATORIO_PRESENCIAL).
    if (localPendente({ formato, local })) return false
    return dayjs(dataFim).isAfter(dayjs(dataInicio))
  }, [titulo, dataInicio, dataFim, formato, local])

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setErro(null)
    try {
      // Com reserva vinculada, o cliente é o da reserva (não se digita de novo).
      const clienteId = reserva ? (reserva.clienteId || null) : clienteSelecionado?.id ?? null
      const clienteNome = reserva
        ? nomeDaReserva(reserva)
        : clienteSelecionado?.razaoSocial ??
          (clienteTextoLivre.trim() ? clienteTextoLivre.trim() : null)

      const payload: ReuniaoRequest = {
        oportunidadeId: oportunidadeId,
        clienteId,
        clienteNome,
        titulo: titulo.trim(),
        descricao: descricao.trim() || null,
        local: local.trim() || null,
        // O PUT é full-replace: o formato vai SEMPRE (criação e edição) — sem ele,
        // editar pela Agenda apagaria o campo em silêncio (condição A06/R01).
        formato: formato || null,
        // O backend é LocalDateTime: Jackson DESCARTA o `Z` sem converter, então
        // toISOString() (UTC) deslocava +3h a cada Salvar em America/Sao_Paulo.
        // Vai o horário local puro, sem fuso — mesma convenção da Nova oportunidade.
        dataInicio: dayjs(dataInicio).format('YYYY-MM-DDTHH:mm:ss'),
        dataFim: dayjs(dataFim).format('YYYY-MM-DDTHH:mm:ss'),
        status,
        observacoes: observacoes.trim() || null,
        primeiroContato,
        participantes: participantes.trim() || null,
      }

      const saved = editing && reuniao
        ? await reuniaoService.atualizar(reuniao.id, payload)
        : await reuniaoService.criar(payload)

      enqueueSnackbar(editing ? 'Reunião atualizada' : 'Reunião agendada', {
        variant: 'success',
      })
      onSaved?.(saved)
      onClose()
    } catch (e: unknown) {
      const msg =
        (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
          ? (e as { message: string }).message
          : null) ?? 'Falha ao salvar reunião'
      setErro(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0, fontWeight: 700 }}>
        {editing ? 'Editar reunião' : 'Agendar reunião'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {erro && <Alert severity="error">{erro}</Alert>}

          {vinculoFixo ? (
            <TextField
              label="Reserva"
              value={
                reserva
                  ? `${reserva.numero != null ? `RES-${String(reserva.numero).padStart(4, '0')} · ` : ''}${nomeDaReserva(reserva)}`
                  : prefill?.clienteNome ?? reuniao?.clienteNome ?? 'Reserva vinculada'
              }
              helperText="A reunião fica vinculada a esta reserva e conta para o avanço dela no funil."
              fullWidth
              InputProps={{ readOnly: true }}
            />
          ) : (
            <SeletorReservaReuniao
              value={reserva}
              onChange={escolherReserva}
              autoFocus={!editing}
            />
          )}

          <TextField
            label="Título"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            fullWidth
            required
            autoFocus={editing || vinculoFixo}
          />

          {oportunidadeId ? (
            <TextField
              label="Cliente"
              value={reserva ? nomeDaReserva(reserva) : clienteTextoLivre}
              helperText={reserva?.cnpj ? `CNPJ ${formatCNPJ(reserva.cnpj)} · da reserva vinculada` : 'Da reserva vinculada'}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          ) : (
          <Autocomplete<Cliente, false, false, true>
            freeSolo
            loading={loadingClientes}
            options={clientes}
            value={clienteSelecionado}
            inputValue={clienteTextoLivre}
            onChange={(_, val) => {
              if (val && typeof val !== 'string') {
                setClienteSelecionado(val)
                setClienteTextoLivre(val.razaoSocial ?? '')
              } else {
                setClienteSelecionado(null)
                setClienteTextoLivre(typeof val === 'string' ? val : '')
              }
            }}
            onInputChange={(_, newInput) => {
              setClienteTextoLivre(newInput)
              if (!newInput) setClienteSelecionado(null)
            }}
            getOptionLabel={(opt) =>
              typeof opt === 'string' ? opt : opt.razaoSocial ?? ''
            }
            renderOption={(props, opt) => (
              <li {...props} key={opt.id}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {opt.razaoSocial}
                  </Typography>
                  {opt.cnpj && (
                    <Typography variant="caption" color="text.secondary">
                      CNPJ: {opt.cnpj}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cliente"
                placeholder="Selecione ou digite o nome do cliente"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingClientes ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Início"
              type="datetime-local"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              label="Término"
              type="datetime-local"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
              error={!!dataInicio && !!dataFim && !dayjs(dataFim).isAfter(dayjs(dataInicio))}
              helperText={
                !!dataInicio && !!dataFim && !dayjs(dataFim).isAfter(dayjs(dataInicio))
                  ? 'Deve ser posterior ao início'
                  : ''
              }
            />
          </Stack>

          {/* Formato + local (Onda 3): mesmos campos da página Nova oportunidade.
              Formato opcional aqui — reuniões antigas não têm o campo. */}
          <ReuniaoFormFields
            exibirDataHora={false}
            formatoObrigatorio={false}
            value={{ dataHora: '', formato, local }}
            onChange={(v) => {
              setFormato(v.formato)
              setLocal(v.local)
            }}
          />

          <FormControl fullWidth>
            <InputLabel id="reuniao-status-label">Status</InputLabel>
            <Select
              labelId="reuniao-status-label"
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value as StatusReuniao)}
            >
              {/* Só as transições que o backend aceita a partir do status ATUAL
                  (nova reunião parte de AGENDADA; terminais não voltam). */}
              {transicoesValidas(reuniao?.status ?? 'AGENDADA').map((s) => (
                <MenuItem key={s} value={s}>
                  {STATUS_LABEL[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Pauta / descrição"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            multiline
            rows={2}
            fullWidth
          />

          <TextField
            label="Participantes"
            value={participantes}
            onChange={(e) => setParticipantes(e.target.value)}
            placeholder="Quem esteve na reunião — inclusive quem não tem acesso à plataforma"
            multiline
            rows={2}
            fullWidth
          />

          {/*
            Etapa 3 da jornada de análise. Não é um rótulo decorativo: concluir a reunião
            marcada assim REINICIA o prazo de 60 dias da reserva, então o texto avisa a
            consequência em vez de deixá-la escondida no backend.
          */}
          <FormControlLabel
            control={
              <Checkbox
                checked={primeiroContato}
                onChange={(e) => setPrimeiroContato(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Primeira reunião com o cliente</Typography>
                <Typography variant="caption" color="textSecondary">
                  Ao marcá-la como realizada, o prazo da reserva é reiniciado em 60 dias.
                </Typography>
              </Box>
            }
          />

          <TextField
            label="Observações / resultado da reunião"
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            placeholder="Anote os pontos discutidos, próximos passos, decisões tomadas…"
            multiline
            rows={3}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
        >
          {submitting ? 'Salvando…' : editing ? 'Salvar' : 'Agendar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
